<template>
  <section
    v-if="isSelectionVisible"
    :class="selectionBlockClasses"
  >
    <SkeletonBlock :is-active="isSkeletonActive">
      <template
        v-if="!props.isExtraSelection"
        #skeleton
      >
        <ExpCardSliderSkeleton
          v-for="i in skeletonSlidersCount"
          :key="`selection-skeleton-${i}`"
          class="exp-selection-skeleton"
          :is-animated="isAnimated"
        />
      </template>

      <TourExpSelectionGrid
        v-if="gridSelection?.length"
        :selection="gridSelection[0]"
        :context-geo-subject="contextGeoSubject"
      />
      <template v-for="(selection, index) in gridSlider">
        <ExpCardSlider
          v-if="showExpSlider(selection)"
          :key="`selection-${index}`"
          :class="expCardSliderCssClass"
          :selection="selection"
          :is-mousewheel-enabled="false"
          sogu-block="section"
          sogu-page="listing"
          :favorite-list="Array.from(favoritesStore.favoriteIds)"
          :context-geo-subject="contextGeoSubject"
          :location="props.location"
          :position="position"
          :tooltip-teleport-target="targetModalElem"
          cardSize="l"
          :showMiniDiscount="isExpCardMiniDiscountActive"
          @toggle-favorite="toggleFavoriteHandle($event, selection)"
        />
      </template>
    </SkeletonBlock>
  </section>
</template>

<script setup lang="ts">
  import { type ContextGeoSubject } from '@/shared/types/geography';
  import type { IExpSelection } from '@/entities/expSelection/model/expSelection.ts';
  import { TourExpSelectionGrid } from '@/widgets/expSelectionsBlock/tour';
  import { SkeletonBlock } from '@experience/core-skeleton-lib';
  import { type LocationType } from '@/entities/location';
  import { ExpCardSlider, ExpCardSliderSkeleton } from '@experience/exp-card';
  import { useExperienceFavorites } from '@/entities/experience/favorite';
  import { useToggleExperienceFavorite } from '@/features/experience-favorite';
  import { useExpCardMiniDiscount } from '@/features/experiments/composables/useExpCardMiniDiscount';
  import type { Experience } from '@experience/exp-card/dist/types/experience';
  import { useToursExperienceFavoriteSogu } from '@/features/experience-favorite/composables/tour/useToursExperienceFavoriteSogu.ts';
  import { computed, type useShadowRoot } from 'vue';
  import { useLoaderStore } from '@/shared/store/useLoaderStore.ts';
  import { useHead } from '@unhead/vue';
  import { useToursSelectionBlock } from '@/widgets/expSelectionsBlock/composables/tour/useToursSelectionBlock';
  import { useSkeletonPrefetchStore } from '@/shared/store/useSkeletonPrefetchStore.ts';

  interface IExpCollectionsBlockProps {
    slug: string;
    location: LocationType;
    contextGeoSubject: ContextGeoSubject;
    page: number;
    isExtraSelection: boolean;
    position: 'top' | 'bottom';
    selectionIndex?: number;
  }

  const loaderStore = useLoaderStore();

  const route = useRoute();
  const isPageLoaded = ref(sessionStorage.getItem('skeleton-lib:page-loaded') === 'true');

  watch(
    () => route.path,
    () => {
      isPageLoaded.value = sessionStorage.getItem('skeleton-lib:page-loaded') === 'true';
    },
  );

  const isAnimated = computed(() => isPageLoaded.value);

  const props = defineProps<IExpCollectionsBlockProps>();

  const MIN_EXP_AMOUNT = 2;
  const DEFAULT_SELECTIONS_SKELETON_COUNT = 1;

  const { getExpSelectionSkeleton } = useSkeletonPrefetchStore();

  const skeletonSlidersCount = computed(() => {
    return getExpSelectionSkeleton(props.location) ?? DEFAULT_SELECTIONS_SKELETON_COUNT;
  });

  const { selectionsWithClearedMovementType, isShowBlock, isFetched } = useToursSelectionBlock(props);
  const { trackSectionFavoriteClick } = useToursExperienceFavoriteSogu();
  const favoritesStore = useExperienceFavorites();
  const { toggle } = useToggleExperienceFavorite();
  const { isExpCardMiniDiscountActive } = useExpCardMiniDiscount();

  const isSelectionVisible = computed(() => {
    return isShowBlock.value && (gridSlider.value.length || isSkeletonActive.value);
  });

  const isSkeletonActive = computed(() => !isFetched.value);

  const rootNode = inject<ReturnType<typeof useShadowRoot>>('shadowRoot', null);
  const modalTarget = ref<HTMLElement | null>(null);

  if (rootNode) {
    modalTarget.value = rootNode.querySelector('.modal-target');
  }

  const selectionBlockClasses = computed(() => {
    return [
      'exp-selection-block',
      {
        'exp-selection-block--tour': !loaderStore.isFiltersLoading,
      },
    ];
  });

  const targetModalElem = computed(() => modalTarget.value || document.body);

  const gridSelection = computed(() =>
    selectionsWithClearedMovementType.value.filter((item) => item?.type === 'tag_to_city'),
  );
  const gridSlider = computed(() => {
    return selectionsWithClearedMovementType.value.filter((item) => item?.type !== 'tag_to_city');
  });

  const showExpSlider = (exp: IExpSelection) => exp.experiences.length > MIN_EXP_AMOUNT;

  const toggleFavoriteHandle = (experience: Experience, selection: IExpSelection) => {
    if (!experience || !experience.id) {
      return;
    }

    trackSectionFavoriteClick(experience, props.contextGeoSubject, selection);
    toggle(experience.id);
  };

  // Preload первого изображения для оптимизации LCP
  const firstImageUrl = computed(() => {
    const firstSlider = gridSlider.value.find((selection) => showExpSlider(selection));

    return firstSlider?.experiences?.[0]?.photos?.[0]?.thumbnail_l;
  });

  const expCardSliderCssClass = computed(() => {
    return 'exp-card-slider';
  });

  useHead({
    link: () => {
      if (!firstImageUrl.value) {
        return [];
      }

      return [
        {
          rel: 'preload',
          as: 'image',
          href: firstImageUrl.value,
          fetchpriority: 'high',
        },
      ];
    },
  });
</script>

<style lang="scss" scoped>
  @use '@experience/shared/dist/assets/adaptivity' as adaptivity;

  .exp-selection-block {
    padding: 48px 0 0;

    :deep(.exp-selection:not(:first-child)),
    :deep(.exp-selection-skeleton:not(:first-child)) {
      margin-top: 48px;
    }

    @include adaptivity.tablet {
      padding: 40px 0 0;

      + :deep(.bottom) {
        .sorting-and-count {
          padding-top: 0;
        }
      }
    }

    @include adaptivity.mobile {
      padding-top: 40px;
    }
  }
</style>
